import { useEffect, useState } from 'preact/hooks';
import Icon from './Icon.jsx';
import { useT } from '../i18n';
import { go } from '../lib/nav';
import { pathOf, rollup, visibleChildren, inScope, LEVELS } from '../data/locations';

// Navigation B + C (decision H0): one navigation state (the node in the URL), two views.
// C = path bar with sibling switcher and level steps; B = tree with counts. Both link to #/loc/<node id>.
export const STATE_COLOR = { crit: 'var(--crit)', alarm: 'var(--alarm)', warn: 'var(--warn)', delayed: 'var(--delayed)', ok: 'var(--ok)', defrost: 'var(--defrost)' };
export const href = n => '/loc/' + n.id;

function stateText(t, r) {
  if (r.crit) return t('st.nCrit', { n: r.crit });
  if (r.alarm) return t('st.nAlarm', { n: r.alarm });
  if (r.warn) return t('st.nWarn', { n: r.warn });
  if (r.delayed) return t('st.nDelayed', { n: r.delayed });
  return t('st.inRange');
}

export function PathBar({ node, scope }) {
  const t = useT();
  const [open, setOpen] = useState(null);
  useEffect(() => { const f = e => { if (!e.target.closest('.crumb')) setOpen(null); }; document.addEventListener('click', f); return () => document.removeEventListener('click', f); }, []);
  const path = pathOf(node);
  return (
    <div class="pathbar" aria-label={t('nav.path')}>
      <span class="crumb"><a class="hb" href={href(path[0])} onClick={e => { e.preventDefault(); go(href(path[0])); }} aria-label={t('nav.home')}><Icon name="home" size={16} /></a></span>
      {path.map((n, i) => {
        const sibs = n.parent ? visibleChildren(n.parent, scope) : [n];
        const r = rollup(n);
        const label = t('lvl.' + n.level + 's');
        return (
          <span key={n.id} style="display:contents">
            <span class="sep">›</span>
            <span class="crumb">
              <button type="button" class={i === path.length - 1 ? 'cur' : ''} aria-expanded={open === n.id} onClick={e => { e.stopPropagation(); setOpen(open === n.id ? null : n.id); }}>
                {n.level === 'site' && <span class="dot" style={{ background: STATE_COLOR[r.worst] }} />}{n.name}<Icon name="chev" size={14} />
              </button>
              {open === n.id && (
                <div class="menu" role="menu">
                  <div class="lbl">{label} {n.parent ? t('nav.in', { name: n.parent.name }) : ''} · {sibs.length}</div>
                  {sibs.map(s => { const rs = rollup(s); return (
                    <button key={s.id} type="button" role="menuitem" class={s.id === n.id ? 'on' : ''} onClick={() => { setOpen(null); go('/loc/' + s.id); }}>
                      <span class="dot" style={{ width: '9px', height: '9px', borderRadius: '50%', background: STATE_COLOR[rs.worst] || 'var(--ok)' }} />{s.name}<span class="mono">{s.level === 'site' || s.level === 'floor' || s.level === 'zone' ? stateText(t, rs) : t('nav.nSites', { n: rs.sites })}</span>
                    </button>); })}
                  {node.children.length > 0 && i === path.length - 1 && <><div class="lbl" style="margin-top:6px">{t('nav.below')}</div>{visibleChildren(n, scope).slice(0, 12).map(c => <button key={c.id} type="button" onClick={() => { setOpen(null); go('/loc/' + c.id); }}>{c.name}<span class="mono">{t('lvl.' + c.level)}</span></button>)}</>}
                </div>
              )}
            </span>
          </span>
        );
      })}
      <div class="levels" aria-label={t('nav.levels')}>
        <span>{LEVELS.map((l, i) => <i key={l} class={i < path.length - 1 ? 'on' : i === path.length - 1 ? 'cur' : ''} />)}</span>
        <span>{t('lvl.' + node.level)} · {t('nav.levelOf', { n: path.length, m: LEVELS.length })}</span>
      </div>
    </div>
  );
}

function TreeNode({ n, cur, scope, depth, problems, t, onSelect }) {
  const onPath = cur.id === n.id || cur.id.startsWith(n.id + '/');
  const [exp, setExp] = useState(onPath || depth < 1);
  useEffect(() => { if (onPath) setExp(true); }, [cur.id]);
  const kids = visibleChildren(n, scope).filter(c => !problems || rollup(c).worst !== 'ok');
  const r = rollup(n); const vis = inScope(n, scope);
  return (
    <li>
      <div class={cur.id === n.id ? 'on' : ''} onClick={() => vis === 'full' ? (onSelect ? onSelect(n.id) : go('/loc/' + n.id)) : setExp(!exp)} style={vis === 'ancestor' ? 'color:var(--muted)' : ''}>
        <span class="tw" onClick={e => { e.stopPropagation(); setExp(!exp); }}>{n.children.length ? (exp ? '▾' : '▸') : ''}</span>
        {(n.level === 'site' || n.level === 'city') && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: STATE_COLOR[r.worst], flex: 'none' }} />}
        <span style="overflow:hidden;text-overflow:ellipsis">{n.name}</span>
        <span class="lv">{t('lvl.' + n.level)}</span>
        {vis === 'full' && n.level !== 'site' && n.level !== 'floor' && n.level !== 'zone' && <span class="cnt">{r.sites}</span>}
      </div>
      {exp && (kids.length > 0 || n.overlays) && (
        <ul>
          {kids.map(c => <TreeNode key={c.id} n={c} cur={cur} scope={scope} depth={depth + 1} problems={problems} t={t} onSelect={onSelect} />)}
          {n.overlays && !problems && <li><div style="font-size:12px;color:var(--muted);cursor:default"><span class="tw" />{t('nav.groupsHere')}</div><ul>{n.overlays.map(o => <li key={o.id}><div title={o.note} class={cur.id === o.id ? 'on' : ''} style="cursor:pointer" onClick={() => onSelect && onSelect(o.id)}><span class="tw">◇</span><i>{o.name}</i><span class="lv">{o.kind}</span><span class="cnt">{o.count}</span></div></li>)}</ul></li>}
        </ul>
      )}
    </li>
  );
}
export function TreePanel({ root, node, scope, onSelect, sticky = true }) {
  const t = useT();
  const [problems, setProblems] = useState(false);
  return (
    <aside class="tree" style={sticky ? '' : 'position:static;height:auto;max-height:720px;border:1px solid var(--line);border-radius:12px'} aria-label={t('nav.tree')}>
      <div class="tree-filter">
        <div class="seg" style="width:100%"><button type="button" class={!problems ? 'on' : ''} style="flex:1" onClick={() => setProblems(false)}>{t('nav.all')}</button><button type="button" class={problems ? 'on' : ''} style="flex:1" onClick={() => setProblems(true)}>{t('nav.problems')}</button></div>
      </div>
      <ul><TreeNode n={root} cur={node} scope={scope} depth={0} problems={problems} t={t} onSelect={onSelect} /></ul>
    </aside>
  );
}
